import type { ColorPaletteConfiguration } from '@/ui/color-picker/ui-color-picker.component';
import type { Branded } from '@/utils/typing/branded.type';
import type { primitive } from '@/utils/typing/primitive.type';
import type { CardShellConfig } from '../data-viz/data-viz-widgets/shells/card/card-shell';
import type { LegacyWidgetCode } from '../providers/rights.constants';
import type {
    BusinessIntelligenceDataSourceCode,
    BusinessIntelligenceDimensionCode,
    BusinessIntelligenceFilter,
    BusinessIntelligenceMeasureCode,
    BusinessIntelligenceRepresentation,
    BusinessIntelligenceTemporalDimensionStep
} from './BusinessIntelligence.interface';
import type { LegacyFrontendWidgetConfig } from './Widget.interface';

export type TailoredWidgetId = Branded<number, 'TailoredWidgetId'>;
export type TailoredWidgetTemplateId = Branded<number, 'TailoredWidgetTemplateId'>;

export enum TailoredWidgetScopeType {
    STANDARD = 'STANDARD',
    PROJECT = 'PROJECT',
    USER = 'USER'
}

export interface TailoredWidgetToSave {
    widgetId: TailoredWidgetId | null;
    name: string;
    type: TailoredWidgetScopeType;
    isInCatalog: boolean;
    canBeMonoSite: boolean;
    canBeMultiSite: boolean;
    configuration?: BusinessIntelligenceWidgetConfiguration;
    widgetTemplateId?: TailoredWidgetTemplateId;
    widgetTemplateParams?: TailoredTemplateWidgetConfigurationParams;
}

export interface TailoredWidgetToCreate {
    name: string;
    type: TailoredWidgetScopeType;
    isInCatalog: boolean;
    canBeMonoSite: boolean;
    canBeMultiSite: boolean;
    configuration?: BusinessIntelligenceWidgetConfiguration;
    widgetTemplateId?: TailoredWidgetTemplateId;
    widgetTemplateParams?: TailoredTemplateWidgetConfigurationParams;
}

export interface TailoredWidgetToEdit {
    name: string;
    isInCatalog: boolean;
    canBeMonoSite: boolean;
    canBeMultiSite: boolean;
    configuration?: BusinessIntelligenceWidgetConfiguration;
    widgetTemplateId?: TailoredWidgetTemplateId;
    widgetTemplateParams?: TailoredTemplateWidgetConfigurationParams;
}

export interface EmptyTailoredWidget {
    name?: string;
    isInCatalog: boolean;
    canBeMonoSite: boolean;
    canBeMultiSite: boolean;
    configuration: TailoredWidgetConfig;
}

export interface TailoredWidget {
    widgetId: TailoredWidgetId;
    name: string;
    code: string;
    type: TailoredWidgetScopeType;
    isInCatalog: boolean;
    canBeMonoSite: boolean;
    canBeMultiSite: boolean;
    thumbnailUrl: string;
    configuration: TailoredWidgetConfig;
    minWidth?: number;
    maxWidth?: number;
    minHeight?: number;
    maxHeight?: number;
}

export interface TailoredWidgetTemplate {
    widgetTemplateId: TailoredWidgetTemplateId;
    code: TailoredWidgetTemplateCode;
    configuration: string;
    canBeMonoSite: boolean;
    canBeMultiSite: boolean;
    thumbnailUrl: string;
}

export type TailoredWidgetConfig = TemplateWidgetConfig | BusinessIntelligenceWidgetConfig | LegacyWidgetConfig | BasicWidgetConfig;

export interface TailoredWidgetConfigBase {
    widgetType: TailoredWidgetType;
}

export interface TemplateWidgetConfig extends TailoredWidgetConfigBase {
    widgetType: TailoredWidgetType.TEMPLATE_WIDGET;
    tailoredWidgetTemplateConfiguration: TemplateWidgetConfiguration;
}

export interface BasicWidgetConfig extends TailoredWidgetConfigBase {
    widgetType: TailoredWidgetType.BASIC_WIDGET;
    basicWidgetConfiguration?: BasicWidgetConfiguration;
}

export interface BusinessIntelligenceWidgetConfig extends TailoredWidgetConfigBase {
    widgetType: TailoredWidgetType.BUSINESS_INTELLIGENCE_WIDGET;
    businessIntelligenceConfiguration: BusinessIntelligenceWidgetConfiguration;
}

// To remove LegacyWidgetConfig interface when the new architecture is ready
export interface LegacyWidgetConfig extends TailoredWidgetConfigBase {
    widgetType: TailoredWidgetType.LEGACY_WIDGET;
    legacyWidgetConfiguration: LegacyWidgetConfiguration;
}

export interface BusinessIntelligenceWidgetConfiguration {
    dataSource: BusinessIntelligenceDataSourceConfiguration;
    representation: BusinessIntelligenceRepresentation;
    shell: BusinessIntelligenceShellConfiguration | null;
}

export interface BusinessIntelligenceDataSourceConfiguration {
    dataSourceCode: BusinessIntelligenceDataSourceCode;
    filters: BusinessIntelligenceFilter[];
}

export interface BusinessIntelligenceMeasureConfiguration {
    measureCode: BusinessIntelligenceMeasureCode;
    color?: ColorPaletteConfiguration | null;
}

export interface BusinessIntelligenceDimensionConfiguration {
    dimensionCode: BusinessIntelligenceDimensionCode;
    groupingParams?: {
        granularity: BusinessIntelligenceTemporalDimensionStep;
        startAt?: number | null;
    } | null;
}

export interface TailoredTemplateWidgetConfigurationParams {
    [key: string]: primitive;
}

export interface TemplateWidgetConfiguration {
    widgetTemplateId: TailoredWidgetTemplateId;
    templateWidgetConfigurationParams: TailoredTemplateWidgetConfigurationParams | null;
    templateWidgetConfigurationExpression?: string;
    templateWidgetConfiguration: any;
}

export interface BasicWidgetConfiguration {
    code: BasicWidgetCode;
    basicWidgetSetting: any;
}

// To remove LegacyWidgetConfiguration interface when the new architecture is ready
export interface LegacyWidgetConfiguration {
    legacyWidgetCode: LegacyWidgetCode;
    legacyConfig: LegacyFrontendWidgetConfig<any, any>;
}

export interface WidgetPosition {
    widgetId: TailoredWidgetId;
    position: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
}

export enum TailoredWidgetType {
    TEMPLATE_WIDGET = 'TEMPLATE_WIDGET',
    BUSINESS_INTELLIGENCE_WIDGET = 'BUSINESS_INTELLIGENCE_WIDGET',
    BASIC_WIDGET = 'BASIC_WIDGET',
    LEGACY_WIDGET = 'LEGACY_WIDGET'
}

export type BusinessIntelligenceShellConfiguration = BusinessIntelligenceCardShellConfiguration;

export enum BusinessIntelligenceShellCode {
    CARD = 'CARD'
}

interface BusinessIntelligenceShellConfigurationBase {
    shellCode: BusinessIntelligenceShellCode;
    shellConfig: any;
}

interface BusinessIntelligenceCardShellConfiguration extends BusinessIntelligenceShellConfigurationBase {
    shellCode: BusinessIntelligenceShellCode.CARD;
    shellConfig: CardShellConfig;
}

export enum TailoredWidgetTemplateCode {
    CUSTOM_IMAGE = 'custom_image',
    CUSTOM_IMAGE_BASE64 = 'custom_image_base64',
    SECTION_TITLE = 'section_title',
    HTML_CONTENT = 'html_content',
    BUSINESS_INDICATOR_GAUGE = 'business_indicator_gauge',
    BUSINESS_INDICATOR_ADVANCEMENT = 'business_indicator_advancement'
}

export enum BasicWidgetCode {
    SCALE = 'scale'
}

export interface TemplateFormChangedEvent {
    isValid: boolean;
    formValues: Record<string, any>;
}
